import {
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-R4SQKVDQ.mjs";

// src/components/button/index.tsx
import {
  Button as AKButton
} from "@ariakit/react";
import { cva } from "class-variance-authority";
import { forwardRef as forwardRef2 } from "react";

// src/util.ts
import { clsx } from "clsx";
import { forwardRef } from "react";
function cn(...inputs) {
  return clsx(inputs);
}
var fixedForwardRef = forwardRef;

// src/components/spinner/index.tsx
import { jsx, jsxs } from "react/jsx-runtime";
function Spinner() {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      className: "r8-spinner",
      xmlns: "http://www.w3.org/2000/svg",
      fill: "none",
      viewBox: "0 0 24 24",
      children: [
        /* @__PURE__ */ jsx(
          "circle",
          {
            style: {
              opacity: 0.25
            },
            cx: "12",
            cy: "12",
            r: "10",
            stroke: "currentColor",
            strokeWidth: "4"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            style: {
              opacity: 0.75
            },
            fill: "currentColor",
            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          }
        )
      ]
    }
  );
}

// src/components/button/index.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var buttonCva = cva("r8-btn", {
  variants: {
    variant: {
      filled: "r8-btn--filled",
      outlined: "r8-btn--outlined",
      clear: "r8-btn--clear"
    },
    loading: {
      true: "r8-btn--disabled"
    },
    disabled: {
      true: "r8-btn--disabled"
    },
    intent: {
      primary: "r8-btn--primary",
      success: "r8-btn--success",
      danger: "r8-btn--danger",
      info: "r8-btn--info",
      warning: "r8-btn--warning",
      brand: "r8-btn--brand"
    },
    underlined: {
      true: "r8-btn--link"
    },
    round: {
      true: "r8-btn--round"
    },
    size: {
      xs: "r8-btn--xs",
      sm: "r8-btn--sm",
      md: "r8-btn--md",
      lg: "r8-btn--lg"
    }
  }
});
var Button = forwardRef2(
  (_a, ref) => {
    var _b = _a, {
      variant = "filled",
      intent = "primary",
      size = "md",
      startIcon,
      endIcon,
      round,
      underlined,
      disabled,
      loading,
      children,
      className
    } = _b, rest = __objRest(_b, [
      "variant",
      "intent",
      "size",
      "startIcon",
      "endIcon",
      "round",
      "underlined",
      "disabled",
      "loading",
      "children",
      "className"
    ]);
    const btnClassName = cn(
      buttonCva({
        variant,
        intent,
        size,
        round,
        underlined,
        loading,
        disabled,
        className
      })
    );
    if (loading) {
      startIcon = /* @__PURE__ */ jsx2(Spinner, {});
      endIcon = null;
    }
    return /* @__PURE__ */ jsxs2(
      AKButton,
      __spreadProps(__spreadValues({}, rest), {
        ref,
        disabled: disabled || loading,
        className: btnClassName,
        children: [
          startIcon ? /* @__PURE__ */ jsx2("span", { className: "r8-btn__icon", children: startIcon }) : null,
          children,
          endIcon ? /* @__PURE__ */ jsx2("span", { className: "r8-btn__icon", children: endIcon }) : null
        ]
      })
    );
  }
);
Button.displayName = "Button";
var IconButton = forwardRef2(
  (_a, ref) => {
    var _b = _a, {
      variant = "filled",
      intent = "primary",
      size = "md",
      loading,
      disabled,
      className,
      children
    } = _b, rest = __objRest(_b, [
      "variant",
      "intent",
      "size",
      "loading",
      "disabled",
      "className",
      "children"
    ]);
    const btnClassName = cn(
      buttonCva({
        variant,
        intent,
        size,
        loading,
        disabled,
        className: `r8-btn--icon ${className}`
      })
    );
    return /* @__PURE__ */ jsx2(
      AKButton,
      __spreadProps(__spreadValues({}, rest), {
        disabled,
        className: btnClassName,
        ref,
        children: /* @__PURE__ */ jsx2("span", { className: "r8-btn__icon", children: loading ? /* @__PURE__ */ jsx2(Spinner, {}) : children })
      })
    );
  }
);
IconButton.displayName = "IconButton";

// src/components/banner/index.tsx
import { X } from "@phosphor-icons/react";
import { forwardRef as forwardRef3 } from "react";

// src/variants.ts
import { cva as cva2 } from "class-variance-authority";
var formGroup = cva2("r8-form-group", {
  variants: {
    size: {
      sm: "r8-form-group--sm",
      md: "r8-form-group--md",
      lg: "r8-form-group--lg"
    },
    variant: {
      error: "r8-form-group--error"
    }
  }
});
var tabs = cva2("r8-tabs", {
  variants: {
    variant: {
      bordered: "r8-tabs--bordered",
      segmented: "r8-tabs--segmented",
      pills: "r8-tabs--pills"
    },
    size: {
      sm: "r8-tabs--sm",
      md: "r8-tabs--md"
    }
  },
  defaultVariants: {
    variant: "bordered",
    size: "md"
  }
});
var badge = cva2("r8-badge", {
  variants: {
    variant: {
      default: "r8-badge--default",
      warning: "r8-badge--warning",
      accent: "r8-badge--accent",
      danger: "r8-badge--danger",
      success: "r8-badge--success"
    },
    size: {
      sm: "r8-badge--sm",
      md: "r8-badge--md"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var box = cva2("r8-box", {
  variants: {
    variant: {
      card: "r8-box--card",
      default: "r8-box--default",
      "tinted-frame": "r8-box--tinted-frame",
      well: "r8-box--well"
    }
  },
  defaultVariants: {
    variant: "default"
  }
});
var selectItem = cva2("r8-select__item", {
  variants: {
    intent: {
      default: "r8-select__item--default",
      danger: "r8-select__item--danger"
    }
  },
  defaultVariants: {
    intent: "default"
  }
});
var dialog = cva2("r8-dialog", {
  variants: {
    variant: {
      default: "r8-dialog--default",
      drawer: "r8-dialog--drawer"
    },
    side: {
      left: "r8-dialog--drawer-left",
      right: "r8-dialog--drawer-right",
      bottom: "r8-dialog--drawer-bottom",
      top: "r8-dialog--drawer-top"
    },
    size: {
      sm: "r8-dialog--sm",
      md: "r8-dialog--md",
      lg: "r8-dialog--lg",
      xl: "r8-dialog--xl"
    }
  },
  defaultVariants: {
    variant: "default",
    side: "right",
    size: "md"
  }
});
var banner = cva2("r8-banner", {
  variants: {
    severity: {
      default: "r8-banner--default",
      info: "r8-banner--info",
      warning: "r8-banner--warning",
      error: "r8-banner--error",
      success: "r8-banner--success"
    }
  },
  defaultVariants: {
    severity: "default"
  }
});

// src/components/banner/index.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "react/jsx-runtime";
var Banner = forwardRef3(
  (_a, ref) => {
    var _b = _a, {
      severity = "default",
      icon,
      heading,
      description,
      meta,
      dismissible,
      onDismiss,
      condensed = false,
      raised = false,
      fullWidth = false,
      flushTop = false,
      centered = false
    } = _b, rest = __objRest(_b, [
      "severity",
      "icon",
      "heading",
      "description",
      "meta",
      "dismissible",
      "onDismiss",
      "condensed",
      "raised",
      "fullWidth",
      "flushTop",
      "centered"
    ]);
    const bannerClassName = cn(
      banner({
        severity
      }),
      {
        "r8-banner--condensed": condensed,
        "r8-banner--raised": raised,
        "r8-banner--full-width": fullWidth,
        "r8-banner--flush-top": flushTop,
        "r8-banner--centered": centered
      }
    );
    return /* @__PURE__ */ jsxs3("div", __spreadProps(__spreadValues({ ref, className: bannerClassName }, rest), { children: [
      dismissible && /* @__PURE__ */ jsx3(
        "button",
        {
          type: "button",
          onClick: () => {
            onDismiss == null ? void 0 : onDismiss();
          },
          className: "r8-banner--dismissible",
          children: /* @__PURE__ */ jsx3(X, { weight: "bold" })
        }
      ),
      icon && /* @__PURE__ */ jsx3("div", { className: "r8-banner__icon", children: icon }),
      /* @__PURE__ */ jsxs3("div", { className: "r8-banner__body", children: [
        /* @__PURE__ */ jsxs3("div", { className: "r8-banner__copy", children: [
          heading ? /* @__PURE__ */ jsx3("div", { className: "r8-banner--heading", children: heading }) : null,
          /* @__PURE__ */ jsx3("div", { className: "r8-banner--description", children: description })
        ] }),
        meta ? /* @__PURE__ */ jsx3("div", { className: "r8-banner__meta", children: meta }) : null
      ] })
    ] }));
  }
);

// src/components/checkbox/index.tsx
import {
  Checkbox as AKCheckbox,
  useCheckboxStore,
  CheckboxProvider
} from "@ariakit/react";
import { jsx as jsx4 } from "react/jsx-runtime";
var Checkbox = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx4(
    AKCheckbox,
    __spreadProps(__spreadValues({
      className: cn("r8-checkbox", props.className)
    }, props), {
      ref
    })
  );
});

// src/components/label/index.tsx
import { cva as cva3 } from "class-variance-authority";
import { forwardRef as forwardRef4 } from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
var labelCva = cva3("r8-label", {
  variants: {
    variant: {
      mono: "r8-label--mono"
    }
  }
});
var Label = forwardRef4(
  (_a, ref) => {
    var _b = _a, { variant, className } = _b, rest = __objRest(_b, ["variant", "className"]);
    const labelClass = labelCva({ variant, className });
    return /* @__PURE__ */ jsx5("label", __spreadProps(__spreadValues({}, rest), { className: labelClass, ref }));
  }
);
Label.displayName = "Label";

// src/components/tabs/index.tsx
import {
  Tab as AKTab,
  TabList as AKTabList,
  TabPanel as AKTabPanel,
  TabProvider,
  useTabContext
} from "@ariakit/react";
import {
  useEffect,
  useRef,
  useState
} from "react";
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
function ResponsiveTabList(props) {
  const _a = props, { children } = _a, rest = __objRest(_a, ["children"]);
  const [overflowing, setOverflowing] = useState(false);
  const tabsContainerRef = useRef(null);
  const context = useTabContext();
  if (!context)
    throw new Error("ResponsiveTabList must be used inside a TabProvider");
  const activeId = context.useState((state) => state.activeId);
  useEffect(() => {
    const tabsContainer = tabsContainerRef.current;
    const checkOverflow = () => {
      if (tabsContainer) {
        if (tabsContainer.scrollWidth > tabsContainer.clientWidth) {
          setOverflowing(true);
        } else {
          setOverflowing(false);
        }
      }
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, []);
  if (!Array.isArray(children))
    return;
  const flattened = children.filter(Boolean).flatMap((child) => {
    var _a2;
    if (Array.isArray((_a2 = child == null ? void 0 : child.props) == null ? void 0 : _a2.children)) {
      return child.props.children;
    }
    return child;
  }).filter(Boolean);
  return /* @__PURE__ */ jsxs4("div", { className: "r8-tabs--responsive-grid", children: [
    /* @__PURE__ */ jsx6(
      "div",
      {
        "aria-hidden": overflowing,
        className: "r8-tabs--responsive-tab-list-wrap",
        style: {
          opacity: overflowing ? 0 : 1,
          pointerEvents: overflowing ? "none" : "auto"
        },
        ref: tabsContainerRef,
        children: /* @__PURE__ */ jsx6(TabList, __spreadProps(__spreadValues({}, rest), { children: flattened.map((child) => {
          return child;
        }) }))
      }
    ),
    overflowing && /* @__PURE__ */ jsx6("div", { className: "r8-tabs--responsive-select-wrap", children: /* @__PURE__ */ jsx6(
      "select",
      {
        value: activeId != null ? activeId : void 0,
        onChange: (e) => {
          context.setActiveId(e.target.value);
        },
        className: "r8-tabs--responsive-select",
        children: flattened.map((tab) => {
          var _a2, _b, _c, _d, _e;
          if (!((_a2 = tab == null ? void 0 : tab.props) == null ? void 0 : _a2.id))
            return null;
          if (!((_b = tab == null ? void 0 : tab.props) == null ? void 0 : _b.children))
            return null;
          return /* @__PURE__ */ jsx6("option", { value: (_d = tab.props) == null ? void 0 : _d.id, children: (_e = tab.props) == null ? void 0 : _e.children }, (_c = tab.props) == null ? void 0 : _c.id);
        })
      }
    ) })
  ] });
}
var TabList = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { size, variant, className, responsive } = _b, rest = __objRest(_b, ["size", "variant", "className", "responsive"]);
    const listClass = cn(tabs({ size, variant }), className);
    if (responsive)
      return /* @__PURE__ */ jsx6(ResponsiveTabList, __spreadValues({ className: listClass }, rest));
    return /* @__PURE__ */ jsx6(AKTabList, __spreadProps(__spreadValues({ className: listClass }, rest), { ref }));
  }
);
var Tab = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className, startIcon, badge: badge2, children } = _b, rest = __objRest(_b, ["className", "startIcon", "badge", "children"]);
    return /* @__PURE__ */ jsxs4(AKTab, __spreadProps(__spreadValues({ className: cn("r8-tabs__tab", className) }, rest), { ref, children: [
      startIcon && /* @__PURE__ */ jsx6("span", { className: "r8-tabs__start-icon", children: startIcon }),
      /* @__PURE__ */ jsx6("span", { children }),
      badge2 && /* @__PURE__ */ jsx6("span", { className: "r8-tabs__badge", children: badge2 })
    ] }));
  }
);
var TabPanel = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
    return /* @__PURE__ */ jsx6(
      AKTabPanel,
      __spreadProps(__spreadValues({
        className: cn("r8-tabs__content", className)
      }, rest), {
        ref
      })
    );
  }
);

// src/components/select/index.tsx
import {
  Select as AKSelect,
  SelectGroup as AKSelectGroup,
  SelectGroupLabel as AKSelectGroupLabel,
  SelectItem as AKSelectItem,
  SelectItemCheck as AKSelectItemCheck,
  SelectList as AKSelectList,
  SelectPopover as AKSelectPopover,
  SelectRow as AKSelectRow,
  SelectProvider,
  useSelectContext
} from "@ariakit/react";
import { Check } from "@phosphor-icons/react";
import { jsx as jsx7 } from "react/jsx-runtime";
var Select = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
    return /* @__PURE__ */ jsx7(AKSelect, __spreadProps(__spreadValues({}, rest), { className: cn("r8-select", className), ref }));
  }
);
var SelectPopover = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  const select = useSelectContext();
  if (!select)
    throw new Error("SelectPopover must be used within a SelectProvider");
  return /* @__PURE__ */ jsx7(
    AKSelectPopover,
    __spreadProps(__spreadValues({
      className: cn("r8-popover", className)
    }, rest), {
      getAnchorRect: (el) => {
        var _a2, _b2;
        const inFormGroup = (_a2 = el == null ? void 0 : el.parentElement) == null ? void 0 : _a2.classList.contains(
          "r8-form-group__input"
        );
        if (inFormGroup) {
          const parentFormGroup = el == null ? void 0 : el.closest(".r8-form-group__control");
          return (_b2 = parentFormGroup == null ? void 0 : parentFormGroup.getBoundingClientRect()) != null ? _b2 : el == null ? void 0 : el.getBoundingClientRect();
        }
        return el == null ? void 0 : el.getBoundingClientRect();
      },
      store: select,
      ref,
      children
    })
  );
});
var SelectItem = fixedForwardRef((_a, ref) => {
  var _b = _a, { className, intent } = _b, rest = __objRest(_b, ["className", "intent"]);
  const itemClass = cn(selectItem({ intent, className }));
  return /* @__PURE__ */ jsx7(AKSelectItem, __spreadProps(__spreadValues({ className: itemClass }, rest), { ref }));
});
var SelectGroup = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx7(
    AKSelectGroup,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group", className)
    }, rest), {
      ref,
      children
    })
  );
});
var SelectGroupLabel = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx7(
    AKSelectGroupLabel,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group-label", className)
    }, rest), {
      ref,
      children
    })
  );
});
var SelectItemCheck = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx7(
    AKSelectItemCheck,
    __spreadProps(__spreadValues({
      className: cn("r8-select__item-check", className)
    }, rest), {
      ref,
      children: /* @__PURE__ */ jsx7(Check, { weight: "bold" })
    })
  );
});
var SelectRow = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx7(
    AKSelectRow,
    __spreadProps(__spreadValues({
      className: cn("r8-select__row", className)
    }, rest), {
      ref
    })
  );
});
var SelectList = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx7(
    AKSelectList,
    __spreadProps(__spreadValues({
      className: cn("r8-select__list", className)
    }, rest), {
      ref
    })
  );
});

// src/components/popover/index.tsx
import {
  Popover as AKPopover,
  PopoverAnchor as AKPopoverAnchor,
  PopoverArrow as AKPopoverArrow,
  PopoverDisclosure as AKPopoverDisclosure,
  PopoverDescription,
  PopoverHeading,
  PopoverProvider,
  usePopoverContext
} from "@ariakit/react";
import { jsx as jsx8 } from "react/jsx-runtime";
var PopoverAnchor = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx8(AKPopoverAnchor, __spreadProps(__spreadValues({}, props), { ref }));
});
var PopoverArrow = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx8(
    AKPopoverArrow,
    __spreadProps(__spreadValues({
      className: cn("r8-popover__arrow", className)
    }, rest), {
      ref
    })
  );
});
var Popover = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  const popover = usePopoverContext();
  return /* @__PURE__ */ jsx8(
    AKPopover,
    __spreadProps(__spreadValues({
      className: cn("r8-popover", className)
    }, rest), {
      store: popover,
      ref,
      children
    })
  );
});
var PopoverDisclosure = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx8(
    AKPopoverDisclosure,
    __spreadProps(__spreadValues({
      className: cn("r8-popover__disclosure", className)
    }, rest), {
      ref
    })
  );
});

// src/components/dialog/index.tsx
import {
  Dialog as AKDialog,
  DialogDescription as AKDialogDescription,
  DialogDisclosure as AKDialogDisclosure,
  DialogDismiss,
  DialogHeading as AKDialogHeading,
  DialogProvider
} from "@ariakit/react";
import { jsx as jsx9 } from "react/jsx-runtime";
var Dialog = fixedForwardRef((_a, ref) => {
  var _b = _a, { className, side, variant, size } = _b, rest = __objRest(_b, ["className", "side", "variant", "size"]);
  const dialogClass = cn(dialog({ variant, side, size, className }));
  const backdropclass = cn("r8-backdrop");
  return /* @__PURE__ */ jsx9(
    AKDialog,
    __spreadProps(__spreadValues({
      backdrop: /* @__PURE__ */ jsx9("div", { className: backdropclass }),
      className: dialogClass
    }, rest), {
      ref
    })
  );
});
var DialogDisclosure = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  if (!dialog)
    throw new Error("DialogDisclosure must be used within a DialogProvider");
  return /* @__PURE__ */ jsx9(
    AKDialogDisclosure,
    __spreadProps(__spreadValues({
      className: cn("r8-dialog__trigger", className)
    }, rest), {
      ref
    })
  );
});
var DialogHeading = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx9(
    AKDialogHeading,
    __spreadProps(__spreadValues({
      className: cn("r8-dialog__heading", className)
    }, rest), {
      ref
    })
  );
});
var DialogDescription = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx9(
    AKDialogDescription,
    __spreadProps(__spreadValues({
      className: cn("r8-dialog__description", className)
    }, rest), {
      ref
    })
  );
});

// src/components/menu/index.tsx
import {
  Menu as AKMenu,
  MenuButton as AKMenuButton,
  MenuGroup as AKMenuGroup,
  MenuGroupLabel as AKMenuGroupLabel,
  MenuItem as AKMenuItem,
  MenuItemCheck as AKMenuItemCheck,
  MenuItemCheckbox as AKMenuItemCheckbox,
  MenuItemRadio as AKMenuItemRadio,
  MenuList as AKMenuList,
  MenuSeparator as AKMenuSeparator,
  MenuProvider
} from "@ariakit/react";
import { Check as Check2 } from "@phosphor-icons/react";
import { jsx as jsx10 } from "react/jsx-runtime";
var Menu = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
    const menuClass = cn("r8-popover", className);
    return /* @__PURE__ */ jsx10(AKMenu, __spreadProps(__spreadValues({}, rest), { className: menuClass, ref }));
  }
);
var MenuButton = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx10(AKMenuButton, __spreadProps(__spreadValues({}, props), { ref }));
});
var MenuItem = fixedForwardRef((_a, ref) => {
  var _b = _a, { intent, className } = _b, rest = __objRest(_b, ["intent", "className"]);
  const itemClass = cn(selectItem({ intent, className }));
  return /* @__PURE__ */ jsx10(AKMenuItem, __spreadProps(__spreadValues({ className: itemClass }, rest), { ref }));
});
var MenuSeparator = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx10(
    AKMenuSeparator,
    __spreadProps(__spreadValues({
      className: cn("r8-select__separator", props.className)
    }, props), {
      ref
    })
  );
});
var MenuItemCheckbox = fixedForwardRef(function MenuItemCheckbox2(_a, ref) {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  const itemClass = cn(selectItem({ className }));
  return /* @__PURE__ */ jsx10(AKMenuItemCheckbox, __spreadProps(__spreadValues({ ref }, rest), { className: itemClass }));
});
var MenuItemCheck = fixedForwardRef(function MenuItemCheck2(_a, ref) {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx10(
    AKMenuItemCheck,
    __spreadProps(__spreadValues({
      className: cn("r8-select__item-check", className),
      ref
    }, rest), {
      children: /* @__PURE__ */ jsx10(Check2, { weight: "bold" })
    })
  );
});
var MenuGroup = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx10(
    AKMenuGroup,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group", className)
    }, rest), {
      ref,
      children
    })
  );
});
var MenuGroupLabel = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx10(
    AKMenuGroupLabel,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group-label", className)
    }, rest), {
      ref,
      children
    })
  );
});
var MenuList = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx10(
    AKMenuList,
    __spreadProps(__spreadValues({
      className: cn("r8-select__list", className)
    }, rest), {
      ref
    })
  );
});
var MenuItemRadio = fixedForwardRef(function MenuItemCheckbox3(_a, ref) {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  const itemClass = cn(selectItem({ className }));
  return /* @__PURE__ */ jsx10(AKMenuItemRadio, __spreadProps(__spreadValues({ ref }, rest), { className: itemClass }));
});

// src/components/combobox/index.tsx
import {
  Combobox as AKCombobox,
  ComboboxGroup as AKComboboxGroup,
  ComboboxGroupLabel as AKComboboxGroupLabel,
  ComboboxItem as AKComboboxItem,
  ComboboxList as AKComboboxList,
  ComboboxPopover as AKComboboxPopover,
  ComboboxProvider,
  useComboboxContext
} from "@ariakit/react";
import { jsx as jsx11 } from "react/jsx-runtime";
var Combobox = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx11(AKCombobox, __spreadProps(__spreadValues({ className: cn("r8-select", className) }, rest), { ref }));
});
var ComboboxPopover = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  const combobox = useComboboxContext();
  if (!combobox)
    throw new Error("ComboboxPopover must be used within Combobox");
  return /* @__PURE__ */ jsx11(
    AKComboboxPopover,
    __spreadProps(__spreadValues({
      className: cn("r8-popover", className)
    }, rest), {
      store: combobox,
      ref,
      getAnchorRect: (el) => {
        var _a2, _b2;
        const inFormGroup = (_a2 = el == null ? void 0 : el.parentElement) == null ? void 0 : _a2.classList.contains(
          "r8-form-group__input"
        );
        if (inFormGroup) {
          const parentFormGroup = el == null ? void 0 : el.closest(".r8-form-group__control");
          return (_b2 = parentFormGroup == null ? void 0 : parentFormGroup.getBoundingClientRect()) != null ? _b2 : el == null ? void 0 : el.getBoundingClientRect();
        }
        return el == null ? void 0 : el.getBoundingClientRect();
      },
      children
    })
  );
});
var ComboboxItem = fixedForwardRef((_a, ref) => {
  var _b = _a, { className, intent } = _b, rest = __objRest(_b, ["className", "intent"]);
  const itemClass = cn(selectItem({ intent, className }));
  return /* @__PURE__ */ jsx11(AKComboboxItem, __spreadProps(__spreadValues({ className: itemClass }, rest), { ref }));
});
var ComboboxGroup = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx11(
    AKComboboxGroup,
    __spreadProps(__spreadValues({
      className: cn("r8-select__group", className)
    }, rest), {
      ref,
      children
    })
  );
});
var ComboboxGroupLabel = fixedForwardRef(({ children, className }, ref) => {
  return /* @__PURE__ */ jsx11(
    AKComboboxGroupLabel,
    {
      className: cn("r8-select__group-label", className),
      ref,
      children
    }
  );
});
var ComboboxList = fixedForwardRef((_a, ref) => {
  var _b = _a, { children, className } = _b, rest = __objRest(_b, ["children", "className"]);
  return /* @__PURE__ */ jsx11(
    AKComboboxList,
    __spreadProps(__spreadValues({
      className: cn("r8-select__list", className)
    }, rest), {
      ref,
      children
    })
  );
});

// src/components/radio-group/index.tsx
import {
  useRadioStore,
  RadioGroup as AKRadioGroup,
  Radio as AKRadio,
  RadioProvider
} from "@ariakit/react";
import { jsx as jsx12 } from "react/jsx-runtime";
var RadioGroup = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, props = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx12(
    AKRadioGroup,
    __spreadProps(__spreadValues({
      className: cn("r8-radio-group", className)
    }, props), {
      ref
    })
  );
});
var Radio = fixedForwardRef(
  (_a, ref) => {
    var _b = _a, { className } = _b, props = __objRest(_b, ["className"]);
    return /* @__PURE__ */ jsx12(
      AKRadio,
      __spreadValues({
        className: cn("r8-radio-group__item", className),
        ref
      }, props)
    );
  }
);

// src/components/tooltip/index.tsx
import {
  Tooltip as AKTooltip,
  TooltipAnchor as AKTooltipAnchor,
  TooltipArrow as AKTooltipArrow,
  TooltipProvider,
  useTooltipContext
} from "@ariakit/react";
import { jsx as jsx13 } from "react/jsx-runtime";
var TooltipAnchor = fixedForwardRef((props, ref) => {
  return /* @__PURE__ */ jsx13(AKTooltipAnchor, __spreadProps(__spreadValues({}, props), { ref }));
});
var TooltipArrow = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, rest = __objRest(_b, ["className"]);
  return /* @__PURE__ */ jsx13(
    AKTooltipArrow,
    __spreadProps(__spreadValues({
      className: cn("r8-tooltip__arrow", className)
    }, rest), {
      ref
    })
  );
});
var Tooltip = fixedForwardRef((_a, ref) => {
  var _b = _a, { className } = _b, props = __objRest(_b, ["className"]);
  const ctx = useTooltipContext();
  return /* @__PURE__ */ jsx13(AKTooltip, __spreadValues({ ref, className: cn("r8-tooltip", className) }, props));
});

// src/components/disclosure/index.tsx
import {
  DisclosureProvider,
  Disclosure as AKDisclosure,
  DisclosureContent as AKDisclosureContent
} from "@ariakit/react";
import { jsx as jsx14 } from "react/jsx-runtime";
var Disclosure = fixedForwardRef((_a, ref) => {
  var _b = _a, { children } = _b, rest = __objRest(_b, ["children"]);
  return /* @__PURE__ */ jsx14(AKDisclosure, __spreadProps(__spreadValues({ className: "r8-disclosure" }, rest), { ref, children }));
});
var DisclosureContent = fixedForwardRef((_a, ref) => {
  var _b = _a, { children } = _b, rest = __objRest(_b, ["children"]);
  return /* @__PURE__ */ jsx14(AKDisclosureContent, __spreadProps(__spreadValues({ className: "r8-disclosure--content", ref }, rest), { children }));
});

// src/components/form/index.tsx
import { CaretDown } from "@phosphor-icons/react";
import { cva as cva4 } from "class-variance-authority";
import { createContext, forwardRef as forwardRef5, useContext } from "react";
import { jsx as jsx15, jsxs as jsxs5 } from "react/jsx-runtime";
var variants = cva4("r8-form-group", {
  variants: {
    size: {
      sm: "r8-form-group--sm",
      md: "r8-form-group--md",
      lg: "r8-form-group--lg"
    },
    severity: {
      error: "r8-form-group--error",
      success: "r8-form-group--success"
    }
  }
});
var FormGroupContext = createContext(null);
var useFormGroupContext = () => {
  const context = useContext(FormGroupContext);
  if (!context) {
    throw new Error("FormGroupControl must be used within a FormGroup");
  }
  return context;
};
function FormGroup({
  children,
  size = "md",
  /**
   * ID is required to associate the label with the input element, as well
   * as the help text.
   */
  id,
  severity
}) {
  const className = variants({ size, severity });
  return /* @__PURE__ */ jsx15(FormGroupContext.Provider, { value: { id }, children: /* @__PURE__ */ jsx15("div", { className, children }) });
}
function FormGroupLabel(props) {
  const { id } = useFormGroupContext();
  return /* @__PURE__ */ jsx15("label", __spreadProps(__spreadValues({ htmlFor: id }, props), { className: "r8-form-group__label", children: props.children }));
}
function FormGroupControl({
  input,
  startAdornment,
  endAdornment,
  endAdornmentOptions
}) {
  return /* @__PURE__ */ jsxs5("div", { className: "r8-form-group__control", children: [
    startAdornment && /* @__PURE__ */ jsx15("div", { className: "r8-form-group__adornment r8-form-group__adornment--start", children: startAdornment }),
    /* @__PURE__ */ jsx15("div", { className: "r8-form-group__input", children: input }),
    endAdornment && /* @__PURE__ */ jsx15(
      "div",
      {
        className: `r8-form-group__adornment r8-form-group__adornment--end ${(endAdornmentOptions == null ? void 0 : endAdornmentOptions.absolute) ? "r8-form-group__adornment--absolute" : ""} ${(endAdornmentOptions == null ? void 0 : endAdornmentOptions.inert) ? "r8-form-group__adornment--inert" : ""}`,
        children: endAdornment
      }
    )
  ] });
}
function FormGroupHelpText({ children }) {
  const { id } = useFormGroupContext();
  return /* @__PURE__ */ jsx15("div", { id: `help-text-${id}`, className: "r8-form-group__help-text", children });
}
FormGroup.Label = FormGroupLabel;
FormGroup.Control = FormGroupControl;
FormGroup.HelpText = FormGroupHelpText;
var TextField = forwardRef5(
  (_a, ref) => {
    var _b = _a, {
      size,
      severity,
      label,
      helpText,
      errorText,
      startAdornment,
      endAdornment,
      id
    } = _b, inputProps = __objRest(_b, [
      "size",
      "severity",
      "label",
      "helpText",
      "errorText",
      "startAdornment",
      "endAdornment",
      "id"
    ]);
    const helpTextId = `help-text-${id}`;
    const textToShow = errorText || helpText;
    return /* @__PURE__ */ jsxs5(FormGroup, { id, size, severity: errorText ? "error" : severity, children: [
      label ? /* @__PURE__ */ jsx15(FormGroup.Label, { htmlFor: id, children: label }) : null,
      /* @__PURE__ */ jsx15(
        FormGroup.Control,
        {
          startAdornment,
          endAdornment,
          input: /* @__PURE__ */ jsx15(
            "input",
            __spreadValues({
              id,
              "aria-describedby": helpTextId,
              ref
            }, inputProps)
          )
        }
      ),
      textToShow ? /* @__PURE__ */ jsx15(FormGroup.HelpText, { children: textToShow }) : null
    ] });
  }
);
var NativeSelect = forwardRef5(
  (_a, ref) => {
    var _b = _a, {
      size,
      severity,
      label,
      helpText,
      errorText,
      startAdornment,
      id
    } = _b, inputProps = __objRest(_b, [
      "size",
      "severity",
      "label",
      "helpText",
      "errorText",
      "startAdornment",
      "id"
    ]);
    const helpTextId = `help-text-${id}`;
    const textToShow = errorText || helpText;
    return /* @__PURE__ */ jsxs5(FormGroup, { id, size, severity: errorText ? "error" : severity, children: [
      label ? /* @__PURE__ */ jsx15(FormGroup.Label, { htmlFor: id, children: label }) : null,
      /* @__PURE__ */ jsx15(
        FormGroup.Control,
        {
          startAdornment,
          endAdornment: /* @__PURE__ */ jsx15("div", { className: "r8-inert", children: /* @__PURE__ */ jsx15(CaretDown, { size: 12, weight: "bold" }) }),
          endAdornmentOptions: {
            absolute: true,
            inert: true
          },
          input: /* @__PURE__ */ jsx15(
            "select",
            __spreadProps(__spreadValues({
              id,
              "aria-describedby": helpTextId
            }, inputProps), {
              ref
            })
          )
        }
      ),
      textToShow ? /* @__PURE__ */ jsx15(FormGroup.HelpText, { children: textToShow }) : null
    ] });
  }
);
var Textarea = forwardRef5(
  (_a, ref) => {
    var _b = _a, { size, severity, label, helpText, errorText, id } = _b, inputProps = __objRest(_b, ["size", "severity", "label", "helpText", "errorText", "id"]);
    {
      const helpTextId = `help-text-${id}`;
      const textToShow = errorText || helpText;
      return /* @__PURE__ */ jsxs5(
        FormGroup,
        {
          id,
          size,
          severity: errorText ? "error" : severity,
          children: [
            label ? /* @__PURE__ */ jsx15(FormGroup.Label, { htmlFor: id, children: label }) : null,
            /* @__PURE__ */ jsx15(
              FormGroup.Control,
              {
                input: /* @__PURE__ */ jsx15(
                  "textarea",
                  __spreadProps(__spreadValues({
                    id,
                    "aria-describedby": helpTextId
                  }, inputProps), {
                    ref
                  })
                ),
                startAdornment: null,
                endAdornment: null
              }
            ),
            textToShow ? /* @__PURE__ */ jsx15(FormGroup.HelpText, { children: textToShow }) : null
          ]
        }
      );
    }
  }
);

// src/components/badge/index.tsx
import { forwardRef as forwardRef6 } from "react";
import { jsx as jsx16, jsxs as jsxs6 } from "react/jsx-runtime";
var Badge = forwardRef6(
  ({ variant, startIcon, children, size = "md", rounded }, ref) => {
    const className = cn(
      badge({ variant, size }),
      rounded ? "r8-badge--rounded" : null
    );
    return /* @__PURE__ */ jsxs6("div", { ref, className, children: [
      startIcon ? /* @__PURE__ */ jsx16("span", { className: "r8-badge__icon", children: startIcon }) : null,
      /* @__PURE__ */ jsx16("span", { children })
    ] });
  }
);
Badge.displayName = "Badge";

// src/components/box/index.tsx
import { forwardRef as forwardRef7 } from "react";
import { jsx as jsx17 } from "react/jsx-runtime";
var Box = forwardRef7(
  (_a, ref) => {
    var _b = _a, { variant, children, className } = _b, rest = __objRest(_b, ["variant", "children", "className"]);
    const boxClass = cn(box({ variant }), className);
    return /* @__PURE__ */ jsx17("div", __spreadProps(__spreadValues({ ref, className: boxClass }, rest), { children }));
  }
);
Box.displayName = "Box";

// src/index.ts
import { VisuallyHidden, Group, GroupLabel, Command } from "@ariakit/react";
export {
  Badge,
  Banner,
  Box,
  Button,
  Checkbox,
  CheckboxProvider,
  Combobox,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxItem,
  ComboboxList,
  ComboboxPopover,
  ComboboxProvider,
  Command,
  Dialog,
  DialogDescription,
  DialogDisclosure,
  DialogDismiss,
  DialogHeading,
  DialogProvider,
  Disclosure,
  DisclosureContent,
  DisclosureProvider,
  FormGroup,
  Group,
  GroupLabel,
  IconButton,
  Label,
  Menu,
  MenuButton,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuItemCheck,
  MenuItemCheckbox,
  MenuItemRadio,
  MenuList,
  MenuProvider,
  MenuSeparator,
  NativeSelect,
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverDescription,
  PopoverDisclosure,
  PopoverHeading,
  PopoverProvider,
  Radio,
  RadioGroup,
  RadioProvider,
  Select,
  SelectGroup,
  SelectGroupLabel,
  SelectItem,
  SelectItemCheck,
  SelectList,
  SelectPopover,
  SelectProvider,
  SelectRow,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabProvider,
  TextField,
  Textarea,
  Tooltip,
  TooltipAnchor,
  TooltipArrow,
  TooltipProvider,
  VisuallyHidden,
  badge,
  banner,
  box,
  dialog,
  formGroup,
  selectItem,
  tabs,
  useCheckboxStore,
  useRadioStore
};
//# sourceMappingURL=index.mjs.map